// SCR-RND-01 Trucks (frozen mockups-rnd-v1, ADR-087): tabs Trucks · Seller changes. The owner registers trucks and
// changes Sellers; the Storekeeper views. Round cash is the owner's (GEN-15).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  EmptyState,
  List,
  ListRow,
  Notice,
  QuickActions,
  SkeletonList,
  SummaryCard,
  Tabs,
  type QuickAction,
} from '../../design/kit';
import { ROUNDS, loadSellerChanges, loadTrucks, type Truck } from '../../data/rounds';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDateTime, formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';
import { useTab } from '../catalog/common';
import { useStockWords } from '../stock/StockSheets';
import { SellerSheet, TruckSheet } from './RoundSheets';

const TABS = ['trucks', 'sellerchanges'] as const;
type Tab = (typeof TABS)[number];

export function useTrucks() {
  return useQuery({ queryKey: [...ROUNDS, 'trucks'], queryFn: loadTrucks });
}

export function StageBadge({ status }: { status: Truck['round_status'] }) {
  const { t } = useTranslation();
  if (!status || status === 'closed') return <Badge>{t('tk.noround')}</Badge>;
  return (
    <Badge tone={status === 'open' ? 'success' : 'warning'}>{t(status === 'open' ? 'st.open' : 'st.ended')}</Badge>
  );
}

export function TrucksPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const owner = role === 'owner';
  const navigate = useNavigate();
  const words = useStockWords();
  const [tab, setTab] = useTab(TABS, 'trucks');
  const [sheet, setSheet] = useState<'new' | 'seller' | null>(null);
  const [editing, setEditing] = useState<Truck | null>(null);
  const trucks = useTrucks();
  const changes = useQuery({
    queryKey: [...ROUNDS, 'changes'],
    queryFn: loadSellerChanges,
    enabled: tab === 'sellerchanges',
  });

  if (trucks.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  const list = trucks.data ?? [];
  const actions: QuickAction[] = owner
    ? [
        { id: 'new', label: t('act.newtruck'), icon: 'plus', main: true, onSelect: () => setSheet('new') },
        ...(list.length
          ? [{ id: 'seller', label: t('act.changeseller'), icon: 'users' as const, onSelect: () => setSheet('seller') }]
          : []),
      ]
    : [];
  return (
    <>
      {trucks.data ? (
        <SummaryCard
          label={t('tk.count')}
          value={String(list.length)}
          figures={[
            {
              label: t('tk.live'),
              value: String(list.filter((x) => x.round_status && x.round_status !== 'closed').length),
            },
            { label: t('tk.loaded'), value: String(list.filter((x) => x.stock.length > 0).length) },
          ]}
        />
      ) : (
        <SkeletonList rows={1} label={t('loading')} />
      )}
      <QuickActions actions={actions} />
      <Tabs
        label={t('p.trucks')}
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        items={TABS.map((id) => ({ id, label: t(`tab.${id}`) }))}
      />
      {tab === 'trucks' ? (
        !trucks.data ? (
          <SkeletonList label={t('loading')} />
        ) : list.length === 0 ? (
          <EmptyState
            icon="truck"
            title={t('empty.trucks')}
            text={t('empty.trucks.text')}
            action={
              owner ? (
                <Button variant="main" icon="plus" onClick={() => setSheet('new')}>
                  {t('act.newtruck')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <List label={t('tab.trucks')}>
            {list.map((k) => (
              <ListRow
                key={k.id}
                testId={`truck-${k.code}`}
                lead={{ icon: 'truck' }}
                title={
                  <>
                    <Code>{k.code}</Code> · {k.category}
                  </>
                }
                onOpen={owner ? () => setEditing(k) : undefined}
                sub={
                  <>
                    <Code>{k.vin}</Code> · {k.seller_name ? <Name>{k.seller_name}</Name> : '—'} ·{' '}
                    {k.ownership === 'external' ? t('tk.externalof', { o: k.owner_name ?? '' }) : t('tk.owned')}
                    <br />
                    {k.stock.length === 0 ? (
                      <span className="muted">{t('tk.empty')}</span>
                    ) : (
                      <span className="yv-chips">
                        {k.stock.slice(0, 3).map((s) => (
                          <span key={s.product_id}>
                            {s.sku ? <Code>{s.sku}</Code> : <Name>{s.product}</Name>} {words.qty(s, s.boxes, s.loose)}
                          </span>
                        ))}
                        {k.stock.length > 3 && <span>{t('tk.more', { n: k.stock.length - 3 })}</span>}
                      </span>
                    )}
                  </>
                }
                value={owner && k.round_cash !== null ? formatMoney(k.round_cash, lang) : undefined}
                badge={
                  <>
                    <StageBadge status={k.round_status} />
                    {k.ownership === 'external' && <Badge tone="info">{t('tk.external')}</Badge>}
                  </>
                }
                actions={
                  <>
                    <Button pill icon="layers" onClick={() => navigate(`/inventory?tab=trucks&truck=${k.id}`)}>
                      {t('act.stock')}
                    </Button>
                    {owner && (
                      <Button pill icon="route" onClick={() => navigate(`/round?truck=${k.id}`)}>
                        {t('act.round')}
                      </Button>
                    )}
                  </>
                }
              />
            ))}
          </List>
        )
      ) : !changes.data ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <List label={t('tab.sellerchanges')}>
          {changes.data.map((c) => (
            <ListRow
              key={c.id}
              lead={{ icon: 'users' }}
              title={
                <>
                  <Code>{c.code}</Code> · {t('sc.from', { a: c.from_name ?? '', b: c.to_name ?? '' })}
                </>
              }
              sub={
                <>
                  {formatDateTime(c.changed_at, lang)} · <span dir="auto">{c.reason}</span>
                  {c.actor_name && (
                    <>
                      {' · '}
                      <Name>{c.actor_name}</Name>
                    </>
                  )}
                </>
              }
            />
          ))}
        </List>
      )}
      {sheet === 'new' && <TruckSheet onClose={() => setSheet(null)} />}
      {sheet === 'seller' && <SellerSheet trucks={list} onClose={() => setSheet(null)} />}
      {editing && <TruckSheet truck={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
